"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";

const services = [
  {
    id: "custom",
    number: "01",
    title: "Proyectos Personalizados",
    subtitle: "Diseñado desde cero para ti",
    description: "Trabajamos tu idea desde el primer boceto hasta la pieza final. Diseño, modelado e impresión completamente adaptados a tus necesidades específicas, dimensiones y materiales.",
    features: ["Consulta inicial gratuita", "Modelado 3D a medida", "Múltiples revisiones", "Entrega en toda Canarias"],
    cta: "Solicitar Proyecto",
    href: "/homepage#contacto",
    accent: "#C4845A",
    bg: "bg-evolvia-bg",
    textColor: "text-evolvia-text-primary",
    dark: true,
  },
  {
    id: "predefined",
    number: "02",
    title: "Diseños Predefinidos",
    subtitle: "Catálogo listo para imprimir",
    description: "Explora nuestro catálogo de diseños ya creados y listos para producción. Esculturas, decoración, maquetas y más. Entrega rápida con la misma calidad premium.",
    features: ["Catálogo amplio", "Entrega en 5–10 días", "Varios materiales", "Precios desde 28€"],
    cta: "Ver Catálogo",
    href: "/products",
    accent: "#C4845A",
    bg: "bg-evolvia-bg-light",
    textColor: "text-evolvia-text-dark",
    dark: false,
  },
  {
    id: "advanced",
    number: "03",
    title: "Personalización Avanzada",
    subtitle: "Tu diseño, nuestra precisión",
    description: "¿Tienes un diseño existente que quieres personalizar? Modificamos, adaptamos y mejoramos diseños de terceros para que se ajusten perfectamente a tus requerimientos.",
    features: ["Modificación de archivos STL", "Adaptación de escala", "Cambio de materiales", "Optimización para impresión"],
    cta: "Consultar Servicio",
    href: "/homepage#contacto",
    accent: "#C4845A",
    bg: "bg-evolvia-bg",
    textColor: "text-evolvia-text-primary",
    dark: true,
  },
];

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const items = ref?.current?.querySelectorAll(".reveal");
    items?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="servicios" ref={ref} className="py-28 px-6 lg:px-10 bg-evolvia-bg relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="text-xs font-700 uppercase tracking-[0.25em] text-evolvia-copper mb-3 block">
            Nuestros Servicios
          </span>
          <h2 className="text-5xl md:text-6xl font-black leading-[0.92] tracking-tight text-evolvia-text-primary mb-5">
            Tres formas de
            <br />
            <span className="gradient-text-copper">trabajar contigo</span>
          </h2>
          <p className="text-evolvia-text-secondary max-w-xl mx-auto leading-relaxed">
            Elige el servicio que mejor se adapta a tu proyecto. Todos con la misma calidad y atención personalizada.
          </p>
        </div>

        {/* Service cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {services?.map((service, i) => (
            <div
              key={service?.id}
              className={`reveal delay-${(i + 1) * 100} relative rounded-sm overflow-hidden group ${
                service?.dark
                  ? "bg-evolvia-card border border-evolvia-border" :"bg-evolvia-bg-light border border-gray-200"
              } p-8 flex flex-col transition-all duration-400 hover:border-evolvia-copper`}
            >
              {/* Number */}
              <span className={`text-xs font-800 tracking-widest mb-6 block ${
                service?.dark ? "text-evolvia-text-muted" : "text-gray-400"
              }`}>
                {service?.number}
              </span>

              {/* Copper accent */}
              <div className="w-10 h-0.5 bg-evolvia-copper mb-6 group-hover:w-20 transition-all duration-500" />

              {/* Title */}
              <h3 className={`text-2xl font-800 mb-1 leading-tight ${
                service?.dark ? "text-evolvia-text-primary" : "text-evolvia-text-dark"
              }`}>
                {service?.title}
              </h3>
              <p className="text-evolvia-copper text-sm font-600 mb-4">{service?.subtitle}</p>

              {/* Description */}
              <p className={`text-sm leading-relaxed mb-6 flex-1 ${
                service?.dark ? "text-evolvia-text-secondary" : "text-gray-500"
              }`}>
                {service?.description}
              </p>

              {/* Features */}
              <ul className="flex flex-col gap-2 mb-8">
                {service?.features?.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-evolvia-copper flex-shrink-0" />
                    <span className={`text-xs ${
                      service?.dark ? "text-evolvia-text-secondary" : "text-gray-500"
                    }`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                href={service?.href}
                className={`text-sm font-700 uppercase tracking-wider flex items-center gap-2 group-hover:gap-3 transition-all duration-300 ${
                  service?.dark ? "text-evolvia-copper" : "text-evolvia-copper"
                }`}
              >
                {service?.cta}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
